import { useState, useEffect } from 'react';
import apiRequest, { handleApiError as baseHandleApiError } from './api/base.js';

const buildQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') {
          searchParams.append(key, item);
        }
      });
      return;
    }

    searchParams.append(key, value);
  });

  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

export const api = {
  // Generic HTTP methods
  post: (endpoint, body, options = {}) => {
    // Ensure body is always a valid JSON string
    const bodyString = body !== undefined && body !== null 
      ? JSON.stringify(body) 
      : null;
    
    return apiRequest(endpoint, {
      method: 'POST',
      ...(bodyString !== null && { body: bodyString }),
      ...options,
    });
  },

  // Products (legacy - uses products table)
  getProducts: (venueId) => apiRequest(`/api/products?venue_id=${venueId}`),
  
  // Menu Items (new - uses menu_items table with RLS)
  getMenuItems: () => apiRequest('/api/menu-items'),

  // Tables
  getTables: (venueId) => apiRequest(`/api/tables?venue_id=${venueId}`),
  getTable: (tableId) => apiRequest(`/api/tables/${tableId}`),
  createTable: (tableData) =>
    apiRequest('/api/tables', {
      method: 'POST',
      body: JSON.stringify(tableData),
    }),
  updateTable: (tableId, tableData) =>
    apiRequest(`/api/tables/${tableId}`, {
      method: 'PUT',
      body: JSON.stringify(tableData),
    }),
  updateTableStatus: (tableId, status) =>
    apiRequest(`/api/tables/${tableId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),
  deleteTable: (tableId) =>
    apiRequest(`/api/tables/${tableId}`, {
      method: 'DELETE',
    }),
  resetAllTables: (venueId) =>
    apiRequest('/api/tables/reset', {
      method: 'POST',
      body: JSON.stringify({ venue_id: venueId }),
    }),

  // Reservations
  getReservations: (params = {}) => apiRequest(`/api/reservations${buildQueryString(params)}`),
  getReservation: (id) => apiRequest(`/api/reservations/${id}`),
  createReservation: (data) => apiRequest('/api/reservations', { method: 'POST', body: JSON.stringify(data) }),
  updateReservation: (id, data) => apiRequest(`/api/reservations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateReservationStatus: (id, status) => apiRequest(`/api/reservations/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  deleteReservation: (id) => apiRequest(`/api/reservations/${id}`, { method: 'DELETE' }),
  getReservationsByTable: (tableId) => apiRequest(`/api/reservations/by-table/${tableId}`),

  // Expenses
  getExpenses: (params = {}) => apiRequest(`/api/expenses${buildQueryString(params)}`),
  getExpense: (id) => apiRequest(`/api/expenses/${id}`),
  createExpense: (data) => apiRequest('/api/expenses', { method: 'POST', body: JSON.stringify(data) }),
  updateExpense: (id, data) => apiRequest(`/api/expenses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteExpense: (id) => apiRequest(`/api/expenses/${id}`, { method: 'DELETE' }),
  getExpenseCategories: () => apiRequest('/api/expenses/categories'),
  getExpenseSummary: (params = {}) => apiRequest(`/api/expenses/summary${buildQueryString(params)}`),

  // Menu / Products (legacy - uses products table)
  createProduct: (productData) =>
    apiRequest('/api/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    }),
  updateProduct: (productId, productData) =>
    apiRequest(`/api/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    }),
  deleteProduct: (productId) =>
    apiRequest(`/api/products/${productId}`, {
      method: 'DELETE',
    }),

  // Server time (for clock synchronization checks)
  getServerTime: () => apiRequest('/api/time'),

  // Menu Items (new - uses menu_items table with RLS)
  // Note: venue_id is automatically set from authenticated user, do not include in payload
  createMenuItem: (menuItemData) =>
    apiRequest('/api/menu-items', {
      method: 'POST',
      body: JSON.stringify(menuItemData),
    }),
  updateMenuItem: (menuItemId, menuItemData) =>
    apiRequest(`/api/menu-items/${menuItemId}`, {
      method: 'PUT',
      body: JSON.stringify(menuItemData),
    }),
  deleteMenuItem: (menuItemId) =>
    apiRequest(`/api/menu-items/${menuItemId}`, {
      method: 'DELETE',
    }),
  reorderMenuItems: (items) =>
    apiRequest('/api/menu-items/reorder', {
      method: 'PATCH',
      body: JSON.stringify({ items }),
    }),

  // Orders
  getOrders: (venueId, tableId, status = null) => {
    const params = new URLSearchParams({ venue_id: venueId });
    if (tableId) params.append('table_id', tableId);
    if (status) params.append('status', status);
    return apiRequest(`/api/orders?${params}`);
  },
  getOrder: (orderId) =>
    apiRequest(`/api/orders/${orderId}`),
  createOrder: (orderData) =>
    apiRequest('/api/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    }),
  addItemsToOrder: (orderId, items) =>
    apiRequest(`/api/orders/${orderId}/items`, {
      method: 'POST',
      body: JSON.stringify({ items }),
    }),
  closeOrder: (orderId) =>
    apiRequest(`/api/orders/${orderId}/close`, {
      method: 'POST',
    }),
  updateOrderStatus: (orderId, status, reason) =>
    apiRequest(`/api/orders/${orderId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, ...(reason && { reason }) }),
    }),
  resetOrder: (orderId, reason) =>
    apiRequest(`/api/orders/${orderId}/reset`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    }),

  sales: {
    open: (payload) =>
      apiRequest('/api/sales', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    byTable: (tableId) => apiRequest(`/api/sales/by-table/${tableId}`),
    addItems: (saleId, items) =>
      apiRequest(`/api/sales/${saleId}/items`, {
        method: 'POST',
        body: JSON.stringify({ items }),
      }),
    voidItem: (saleId, itemId, reason) =>
      apiRequest(`/api/sales/${saleId}/items/${itemId}/void`, {
        method: 'PATCH',
        body: JSON.stringify({ reason }),
      }),
    storno: (saleId, reason) =>
      apiRequest(`/api/sales/${saleId}/storno`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
  },

  // Order Items
  updateOrderItemStatus: (itemId, status) =>
    apiRequest(`/api/order-items/${itemId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),
  deleteOrderItem: (itemId) =>
    apiRequest(`/api/order-items/${itemId}`, {
      method: 'DELETE',
    }),

  auth: {
    identify: (email) =>
      apiRequest('/api/auth/identify', {
        method: 'POST',
        body: JSON.stringify({ email }),
        skipAuth: true,
      }),
    login: ({ venuePin, pin, deviceToken, deviceLabel, email } = {}) =>
      apiRequest('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ venuePin, pin, deviceToken, deviceLabel, ...(email ? { email } : {}) }),
        skipAuth: true,
      }),
    me: () => apiRequest('/api/auth/me'),
    logout: () =>
      apiRequest('/api/auth/logout', {
        method: 'POST',
      }),
    myVenues: () => apiRequest('/api/auth/my-venues'),
  },

  // Fiscal unit status
  fiscal: {
    status: () => apiRequest('/api/fiscal/status'),
  },
  
  // Generic GET helper for custom endpoints (used for audit logs)
  get: (endpoint) => apiRequest(endpoint),
  
  // Analytics
  analytics: {
    overview: {
      getRevenue: ({
        venueId,
        start,
        end,
        groupBy,
        page,
        pageSize,
      } = {}) =>
        apiRequest(
          `/api/analytics/revenue${buildQueryString({
            venue_id: venueId,
            start,
            end,
            group_by: groupBy,
            page,
            page_size: pageSize,
          })}`
        ),
      getTopItems: ({
        venueId,
        start,
        end,
        category,
        search,
        sortBy,
        direction,
        page,
        pageSize,
      } = {}) =>
        apiRequest(
          `/api/analytics/top-items${buildQueryString({
            venue_id: venueId,
            start,
            end,
            category,
            search,
            sort_by: sortBy,
            direction,
            page,
            page_size: pageSize,
          })}`
        ),
      getInventoryAlerts: ({
        venueId,
        start,
        end,
        severity,
        thresholdDays,
        variancePctThreshold,
        page,
        pageSize,
      } = {}) =>
        apiRequest(
          `/api/analytics/inventory-alerts${buildQueryString({
            venue_id: venueId,
            start,
            end,
            severity,
            threshold_days: thresholdDays,
            variance_pct_threshold: variancePctThreshold,
            page,
            page_size: pageSize,
          })}`
        ),
      getStaffPerformance: ({
        venueId,
        start,
        end,
        sortBy,
        direction,
        page,
        pageSize,
      } = {}) =>
        apiRequest(
          `/api/analytics/staff-performance${buildQueryString({
            venue_id: venueId,
            start,
            end,
            sort_by: sortBy,
            direction,
            page,
            page_size: pageSize,
          })}`
        ),
    },
    sales: {
      getDaily: (venueId, date) =>
        apiRequest(
          `/api/analytics/sales/daily${buildQueryString({
            venue_id: venueId,
            date,
          })}`
        ),
      getHourly: (venueId, date) =>
        apiRequest(
          `/api/analytics/sales/hourly${buildQueryString({
            venue_id: venueId,
            date,
          })}`
        ),
      getTopProducts: ({ venueId, limit, days, sortBy } = {}) =>
        apiRequest(
          `/api/analytics/sales/top-products${buildQueryString({
            venue_id: venueId,
            limit,
            days,
            sort_by: sortBy,
          })}`
        ),
      getPaymentMix: (venueId, days) =>
        apiRequest(
          `/api/analytics/sales/payment-mix${buildQueryString({
            venue_id: venueId,
            days,
          })}`
        ),
      getForecast: (venueId) =>
        apiRequest(
          `/api/analytics/sales/forecast${buildQueryString({
            venue_id: venueId,
          })}`
        ),
    },
    inventory: {
      getStatus: (venueId) =>
        apiRequest(
          `/api/analytics/inventory/status${buildQueryString({
            venue_id: venueId,
          })}`
        ),
      getReorder: (venueId) =>
        apiRequest(
          `/api/analytics/inventory/reorder${buildQueryString({
            venue_id: venueId,
          })}`
        ),
      getABC: (venueId) =>
        apiRequest(
          `/api/analytics/inventory/abc${buildQueryString({
            venue_id: venueId,
          })}`
        ),
    },
    operations: {
      getTiming: (venueId, days) =>
        apiRequest(
          `/api/analytics/operations/timing${buildQueryString({
            venue_id: venueId,
            days,
          })}`
        ),
      getUtilization: (venueId, date) =>
        apiRequest(
          `/api/analytics/operations/utilization${buildQueryString({
            venue_id: venueId,
            date,
          })}`
        ),
    },
    staff: {
      getPerformance: (venueId, days) =>
        apiRequest(
          `/api/analytics/staff/performance${buildQueryString({
            venue_id: venueId,
            days,
          })}`
        ),
    },
    financial: {
      getSummary: (venueId, days) =>
        apiRequest(
          `/api/analytics/financial/summary${buildQueryString({
            venue_id: venueId,
            days,
          })}`
        ),
      getProducts: (venueId, days) =>
        apiRequest(
          `/api/analytics/financial/products${buildQueryString({
            venue_id: venueId,
            days,
          })}`
        ),
    },
    alerts: {
      getAll: ({ venueId, days } = {}) =>
        apiRequest(
          `/api/analytics/alerts/all${buildQueryString({
            venue_id: venueId,
            days,
          })}`
        ),
      calculatePromoUplift: ({ venueId, promoStartDate, promoEndDate }) =>
        apiRequest('/api/analytics/alerts/promo', {
          method: 'POST',
          body: JSON.stringify({
            venue_id: venueId,
            promo_start_date: promoStartDate,
            promo_end_date: promoEndDate,
          }),
        }),
      calculateElasticity: ({ productId, venueId, priceChangeDate }) =>
        apiRequest('/api/analytics/alerts/elasticity', {
          method: 'POST',
          body: JSON.stringify({
            product_id: productId,
            venue_id: venueId,
            price_change_date: priceChangeDate,
          }),
        }),
    },
  },

  // Staff
  getMyReport: (date) =>
    apiRequest(
      `/api/staff/my-report${buildQueryString({ date })}`
    ),
  getStaffRoster: (venueId) =>
    apiRequest(
      `/api/staff/roster${buildQueryString({ venue_id: venueId })}`
    ),
  getClockStatus: (staffId, venueId) =>
    apiRequest(
      `/api/staff/clock-status${buildQueryString({ staff_id: staffId, venue_id: venueId })}`
    ),
  clockIn: (staffId, venueId) =>
    apiRequest('/api/staff/clock-in', {
      method: 'POST',
      body: JSON.stringify({ staff_id: staffId, venue_id: venueId }),
    }),
  clockOut: (staffId, venueId) =>
    apiRequest('/api/staff/clock-out', {
      method: 'POST',
      body: JSON.stringify({ staff_id: staffId, venue_id: venueId }),
    }),
  getStaffShifts: (staffId, venueId, month) =>
    apiRequest(
      `/api/staff/shifts${buildQueryString({ staff_id: staffId, venue_id: venueId, ...(month ? { month } : {}) })}`
    ),
  adjustShift: (shiftId, data) =>
    apiRequest(`/api/staff/shifts/${shiftId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  hireStaff: (staffData) =>
    apiRequest('/api/staff/hire', {
      method: 'POST',
      body: JSON.stringify(staffData),
    }),
  fireStaff: (userId) =>
    apiRequest(`/api/staff/${userId}/fire`, {
      method: 'POST',
    }),
  updateStaff: (userId, staffData) =>
    apiRequest(`/api/staff/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(staffData),
    }),

  // Inventory (new standalone inventory system)
  getInventory: () => apiRequest('/api/inventory'),
  getInventoryAlerts: (acknowledged = false) => 
    apiRequest(`/api/inventory/alerts?acknowledged=${acknowledged}`),
  createInventoryItem: (itemData) =>
    apiRequest('/api/inventory', {
      method: 'POST',
      body: JSON.stringify(itemData),
    }),
  updateInventoryItem: (itemId, itemData) =>
    apiRequest(`/api/inventory/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(itemData),
    }),
  deleteInventoryItem: (itemId) =>
    apiRequest(`/api/inventory/${itemId}`, {
      method: 'DELETE',
    }),
  addInventoryQuantity: (itemId, data) =>
    apiRequest(`/api/inventory/${itemId}/add`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  deductInventoryQuantity: (itemId, data) =>
    apiRequest(`/api/inventory/${itemId}/deduct`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  acknowledgeInventoryAlert: (alertId) =>
    apiRequest(`/api/inventory/alerts/${alertId}/acknowledge`, {
      method: 'POST',
    }),
  getInventoryLocations: () => apiRequest('/api/inventory/locations'),
  createInventoryLocation: (data) =>
    apiRequest('/api/inventory/locations', { method: 'POST', body: JSON.stringify(data) }),
  updateInventoryLocation: (id, data) =>
    apiRequest(`/api/inventory/locations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteInventoryLocation: (id) =>
    apiRequest(`/api/inventory/locations/${id}`, { method: 'DELETE' }),
  getInventoryMode: () => apiRequest('/api/inventory/mode'),
  setInventoryMode: (mode) =>
    apiRequest('/api/inventory/mode', { method: 'PUT', body: JSON.stringify({ mode }) }),
  transferInventory: (itemId, data) =>
    apiRequest(`/api/inventory/${itemId}/transfer`, { method: 'POST', body: JSON.stringify(data) }),
  getInventoryLots: (itemId, activeOnly = true) =>
    apiRequest(`/api/inventory/${itemId}/lots?active_only=${activeOnly}`),
  getInventoryCost: (itemId) =>
    apiRequest(`/api/inventory/${itemId}/cost`),

  // Suppliers
  suppliers: {
    list: (activeOnly = true) =>
      apiRequest(`/api/suppliers?active_only=${activeOnly}`),
    get: (id) => apiRequest(`/api/suppliers/${id}`),
    create: (data) =>
      apiRequest('/api/suppliers', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id, data) =>
      apiRequest(`/api/suppliers/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id) =>
      apiRequest(`/api/suppliers/${id}`, {
        method: 'DELETE',
      }),
  },

  // Deliveries
  deliveries: {
    list: (params = {}) =>
      apiRequest(`/api/deliveries${buildQueryString(params)}`),
    get: (id) => apiRequest(`/api/deliveries/${id}`),
    create: (data) =>
      apiRequest('/api/deliveries', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id, data) =>
      apiRequest(`/api/deliveries/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id) =>
      apiRequest(`/api/deliveries/${id}`, {
        method: 'DELETE',
      }),
    verifyLine: (deliveryId, lineId, data) =>
      apiRequest(`/api/deliveries/${deliveryId}/lines/${lineId}/verify`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    verifyAll: (deliveryId) =>
      apiRequest(`/api/deliveries/${deliveryId}/verify-all`, {
        method: 'POST',
      }),
  },

  // Venue Settings
  venueSettings: {
    get: () => apiRequest('/api/settings/venue'),
    update: (data) =>
      apiRequest('/api/settings/venue', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
  },

  // Payments
  createPayment: (paymentData) =>
    apiRequest('/api/payments', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    }),

  // Discounts
  discounts: {
    list: (includeInactive = false) =>
      apiRequest(`/api/discounts${includeInactive ? '?includeInactive=true' : ''}`),
    get: (discountId) => apiRequest(`/api/discounts/${discountId}`),
    create: (discountData) =>
      apiRequest('/api/discounts', {
        method: 'POST',
        body: JSON.stringify(discountData),
      }),
    update: (discountId, discountData) =>
      apiRequest(`/api/discounts/${discountId}`, {
        method: 'PUT',
        body: JSON.stringify(discountData),
      }),
  },
};

export const usePolling = (endpoint, interval = 2000, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(endpoint));
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await apiRequest(endpoint);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!endpoint) {
      setLoading(false);
      return;
    }

    fetchData();

    if (interval > 0) {
      const intervalId = setInterval(fetchData, interval);
      return () => clearInterval(intervalId);
    }

    return undefined;
  }, [endpoint, interval, ...dependencies]);

  return { data, loading, error, refetch: fetchData };
};

export const handleApiError = baseHandleApiError;

export const getAnalytics = api.analytics;

export default api;
